import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  LinearProgress,
} from '@mui/material';
import { toast } from 'react-toastify';
// Components
import TableTextField from './TableTextField';
import ChildInformation from './ChildInformation';
// Context
import { useMdProfileContext } from '../context/useMdProfileContext';
// API
import { getDoctorChildInfo, getDoctorInfo } from '../../../api/mdProfileApi';

const cellHeader = {
  backgroundColor: '#b5d7b5',
  fontWeight: 'bold',
  border: '1px solid #999',
  width: '15%',
  whiteSpace: 'nowrap',
};

export default function BasicInformationTab() {
  const { form, setForm, viewProfile } = useContext(useMdProfileContext);

  const editable = false;
  const [loading, setLoading] = useState(false);

  const cellInput = {
    border: '1px solid #999',
    backgroundColor: editable ? '#f9fff9' : '#ebeeebff',
    padding: 0,
    height: 52,
  };

  const loadDoctorInfo = async () => {
    try {
      setLoading(true);

      const request = {
        doctor_id: viewProfile?.doctor_id,
        info_type: 'basic_info',
      };

      const response = await getDoctorInfo(request);
      const { code, data } = response.data;

      if (code === '200') {
        setLoading(false);
        setForm((prevState) => ({
          ...prevState,
          ...data,
        }));
      }
    } catch (error) {
      toast.error(error);
    }
  };

  const loadDoctorChildInfo = async () => {
    try {
      const request = {
        doctor_id: viewProfile?.doctor_id,
      };

      await getDoctorChildInfo(request).then((response) => {
        const newState = response.data.data;
        setForm((prevState) => ({
          ...prevState,
          childrens: newState,
        }));
      });
    } catch (error) {
      toast.error(error);
    }
  };

  useEffect(() => {
    loadDoctorInfo();
    loadDoctorChildInfo();
  }, [viewProfile]);

  return (
    <Box>
      {loading ? (
        <LinearProgress />
      ) : (
        <>
          <TableContainer component={Paper} sx={{ width: '100%' }}>
            <Table>
              <TableBody>
                {/* Row 1 */}
                <TableRow>
                  <TableCell sx={cellHeader}>Name</TableCell>
                  <TableCell sx={cellInput}>
                    <TableTextField name='given_name' value={form.given_name} editable={editable} />
                  </TableCell>
                  <TableCell sx={cellInput} colSpan={2}>
                    <TableTextField
                      name='middle_name'
                      value={form.middle_name}
                      editable={editable}
                    />
                  </TableCell>
                  <TableCell sx={cellInput} colSpan={2}>
                    <TableTextField name='surname' value={form.surname} editable={editable} />
                  </TableCell>
                  <TableCell sx={cellInput}>
                    <TableTextField name='nickname' value={form.nickname} editable={editable} />
                  </TableCell>
                </TableRow>

                {/* Row 2 */}
                <TableRow>
                  <TableCell sx={cellHeader}>Specialty</TableCell>
                  <TableCell sx={cellInput}>
                    <TableTextField
                      name='specialty'
                      value={form.specialty}
                      placeholder='N/A'
                      editable={editable}
                    />
                  </TableCell>
                  <TableCell sx={cellHeader}>Sub-Specialty</TableCell>
                  <TableCell sx={cellInput} colSpan={2}>
                    <TableTextField
                      name='sub_specialty'
                      value={form.sub_specialty}
                      placeholder='N/A'
                      editable={editable}
                    />
                  </TableCell>
                  <TableCell sx={cellHeader}>Practicing</TableCell>
                  <TableCell sx={cellInput}>
                    <TableTextField value='' placeholder='N/A' editable={editable} />
                  </TableCell>
                </TableRow>

                {/* Row 3 */}
                <TableRow>
                  <TableCell sx={cellHeader}>KASS/SAR</TableCell>
                  <TableCell colSpan={3} sx={cellInput}>
                    <TableTextField value='' placeholder='N/A' editable={editable} />
                  </TableCell>
                  <TableCell colSpan={2} sx={cellHeader}>
                    Joined BK
                  </TableCell>
                  <TableCell sx={{ ...cellInput, color: 'gray' }}>
                    <TableTextField
                      type='text'
                      name='date_joined'
                      value={form.date_joined}
                      placeholder='Date'
                      editable={editable}
                      hideable
                    />
                  </TableCell>
                </TableRow>

                {/* Row 4 */}
                <TableRow>
                  <TableCell sx={cellHeader}>Group</TableCell>
                  <TableCell colSpan={3} sx={cellInput}>
                    <TableTextField
                      name='group'
                      value={form.group}
                      placeholder='N/A'
                      editable={editable}
                    />
                  </TableCell>
                  <TableCell sx={cellHeader}>Previous Group</TableCell>
                  <TableCell colSpan={2} sx={cellInput}>
                    <TableTextField value='' placeholder='N/A' editable={editable} />
                  </TableCell>
                </TableRow>

                {/* Row 5 */}
                <TableRow>
                  <TableCell sx={cellHeader} rowSpan={2}>
                    Present Address
                  </TableCell>
                  <TableCell sx={cellInput} colSpan={2}>
                    <TableTextField
                      name='present_address_house_bldg_no'
                      value={form.present_address_house_bldg_no}
                      placeholder='House/St./Bldg No.'
                      editable={editable}
                      hideable
                    />
                  </TableCell>
                  <TableCell sx={{ ...cellInput, color: 'gray' }} colSpan={2}>
                    <TableTextField
                      name='present_address_st_subd_name'
                      value={form.present_address_st_subd_name}
                      placeholder='St./Bldg/Subd. Name'
                      editable={editable}
                      hideable
                    />
                  </TableCell>
                  <TableCell sx={{ ...cellInput, color: 'gray' }} colSpan={2}>
                    <TableTextField
                      name='present_address_st_subd_name'
                      value={form.present_address_st_subd_name}
                      placeholder='City'
                      editable={editable}
                      hideable
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ ...cellInput, height: 52 }} colSpan={4}>
                    <TableTextField value='' placeholder='Province' editable={editable} hideable />
                  </TableCell>
                  <TableCell sx={{ ...cellInput }} colSpan={2}>
                    <TableTextField
                      name='present_address_zip_code'
                      value={form.present_address_zip_code}
                      placeholder='Zip Code'
                      editable={editable}
                      hideable
                    />
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <TableContainer component={Paper} sx={{ width: '100%' }}>
            <Table>
              <TableBody>
                {/*  */}
                <TableRow>
                  <TableCell sx={cellHeader} rowSpan={2}>
                    Delivery Address
                  </TableCell>
                  <TableCell sx={cellInput} colSpan={2}>
                    <TableTextField
                      name='delivery_address_house_bldg_no'
                      value={form.delivery_address_house_bldg_no}
                      placeholder='House/St./Bldg No.'
                      editable={editable}
                      hideable
                    />
                  </TableCell>
                  <TableCell sx={{ ...cellInput, color: 'gray' }} colSpan={2}>
                    <TableTextField
                      name='delivery_address_st_subd_name'
                      value={form.delivery_address_st_subd_name}
                      placeholder='St./Bldg/Subd. Name'
                      editable={editable}
                      hideable
                    />
                  </TableCell>
                  <TableCell sx={{ ...cellInput, color: 'gray' }} colSpan={2}>
                    <TableTextField value='' placeholder='City' editable={editable} hideable />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ ...cellInput, height: 52 }} colSpan={4}>
                    <TableTextField value='' placeholder='Province' editable={editable} hideable />
                  </TableCell>
                  <TableCell sx={{ ...cellInput }} colSpan={2}>
                    <TableTextField
                      name='delivery_address_zip_code'
                      value={form.delivery_address_zip_code}
                      placeholder='Zip Code'
                      editable={editable}
                      hideable
                    />
                  </TableCell>
                </TableRow>
                {/*  */}
                <TableRow>
                  <TableCell sx={cellHeader}>Date of Birth</TableCell>
                  <TableCell sx={cellInput}>
                    <TableTextField
                      type='text'
                      name='dob'
                      value={form.dob}
                      placeholder='N/A'
                      editable={editable}
                      hideable
                    />
                  </TableCell>
                  <TableCell sx={cellHeader}>Age</TableCell>
                  <TableCell sx={cellInput} colSpan={2}>
                    <TableTextField value='' placeholder='N/A' editable={editable} hideable />
                  </TableCell>
                  <TableCell sx={cellHeader}>Civil Status</TableCell>
                  <TableCell sx={cellInput}>
                    <TableTextField
                      name='civil_status'
                      value={form.civil_status}
                      placeholder='N/A'
                      editable={editable}
                    />
                  </TableCell>
                </TableRow>
                {/*  */}
                <TableRow sx={{ backgroundColor: '#f0df47ff' }}>
                  <TableCell colSpan='100%' sx={{ fontWeight: 'bold', textAlign: 'center' }}>
                    SPOUSE INFORMATION
                  </TableCell>
                </TableRow>
                {/*  */}
                <TableRow>
                  <TableCell sx={cellHeader}>Spouse Name</TableCell>
                  <TableCell sx={cellInput}>
                    <TableTextField
                      name='surname'
                      value={form.surname}
                      placeholder='Surname'
                      editable={editable}
                    />
                  </TableCell>
                  <TableCell sx={cellInput}>
                    <TableTextField
                      name='spouse_given_name'
                      value={form.spouse_given_name}
                      placeholder='Given Name'
                      editable={editable}
                    />
                  </TableCell>
                  <TableCell sx={cellHeader}>Wedding Anniversary</TableCell>
                  <TableCell sx={cellInput} colSpan={3}>
                    <TableTextField
                      type='text'
                      name='wedding_anniv'
                      value={form.wedding_anniv}
                      placeholder='N/A'
                      editable={editable}
                      hideable
                    />
                  </TableCell>
                </TableRow>
                {/*  */}
                <TableRow>
                  <TableCell sx={cellHeader}>Spouse's Occupation</TableCell>
                  <TableCell sx={cellInput} colSpan={2}>
                    <TableTextField
                      name='spouse_occupation'
                      value={form.spouse_occupation}
                      placeholder='Occupation'
                      editable={editable}
                    />
                  </TableCell>
                  <TableCell sx={cellHeader}>Date of Birth</TableCell>
                  <TableCell sx={cellInput} colSpan={3}>
                    <TableTextField
                      type='text'
                      value={form.spouse_dob}
                      placeholder='N/A'
                      editable={editable}
                      hideable
                    />
                  </TableCell>
                </TableRow>
                {/*  */}
                <TableRow>
                  <TableCell sx={cellHeader}>Specialty (if MD)</TableCell>
                  <TableCell sx={cellInput} colSpan={2}>
                    <TableTextField
                      value={form.spouse_specialty}
                      placeholder='Spouse Specialty'
                      editable={editable}
                    />
                  </TableCell>
                  <TableCell sx={cellHeader}>School/Hospital</TableCell>
                  <TableCell sx={cellInput} colSpan={3}>
                    <TableTextField
                      value={form.spouse_school_hospital}
                      placeholder='School/Hospital'
                      editable={editable}
                    />
                  </TableCell>
                </TableRow>
                {/*  */}
                <TableRow sx={{ backgroundColor: '#f0df47ff' }}>
                  <TableCell colSpan='100%' sx={{ fontWeight: 'bold', textAlign: 'center' }}>
                    PARENT INFORMATION
                  </TableCell>
                </TableRow>
                {/*  */}
                <TableRow>
                  <TableCell sx={cellHeader}>Father's Name</TableCell>
                  <TableCell sx={cellInput} colSpan={2}>
                    <TableTextField
                      value={form.father_name}
                      placeholder="Father's Name"
                      editable={editable}
                    />
                  </TableCell>
                  <TableCell sx={cellHeader}>Mother's Name</TableCell>
                  <TableCell sx={cellInput} colSpan={3}>
                    <TableTextField
                      value={form.mother_name}
                      placeholder="Mother's Name"
                      editable={editable}
                    />
                  </TableCell>
                </TableRow>
                {/*  */}
                <TableRow sx={{ backgroundColor: '#f0df47ff' }}>
                  <TableCell colSpan='100%' sx={{ fontWeight: 'bold', textAlign: 'center' }}>
                    CHILD/CHILDREN INFORMATION
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ padding: 0 }} colSpan={'100%'}></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          <ChildInformation {...{ form, editable, cellHeader, cellInput }} />
        </>
      )}
    </Box>
  );
}
